import { http } from '@/api/client/httpClient';

// IF1AppService -> /api/app/i-f1/*. Quarter enum Q1=1..Q4=4.
export interface ProfileIF1Dto {
  id: string;
  year: number;
  quarter: number;
  quarterStartDate: string;
  concurrencyStamp: string;
  [prop: string]: number | string;
}
export interface If1EnsureQuartersInput {
  currentYear: number;
  currentMonth: number;
}
export interface ProfileIF1UpdateDto {
  id: string;
  concurrencyStamp: string;
  [prop: string]: number | string;
}

const BASE = '/api/app/i-f1';
export const if1Api = {
  ensureQuarters: (input: If1EnsureQuartersInput) => http.post<number>(`${BASE}/ensure-quarters`, input).then((r) => r.data),
  // ABP maps GetListAsync to the collection root (GET /api/app/i-f1), NOT /list.
  // Calling /list bound "list" to the GetAsync(Guid id) route param → ModelState
  // "The value 'list' is not valid." (see [[feedback_abp_id_in_path_routing]]).
  getList: () => http.get<ProfileIF1Dto[]>(BASE, { params: { sorting: 'QuarterStartDate desc', skipCount: 0, maxResultCount: 1000 } }).then((r) => r.data),
  updateMany: (updates: ProfileIF1UpdateDto[]) => http.put(`${BASE}/many`, updates).then((r) => r.data),
};

export interface If1Row {
  prop: string;
  label: string;
  editable: boolean;
  neg: boolean;
}

// Exact legacy order (row code = (index+1)*10). Labels verbatim incl. intentional typos
// and trailing/double spaces. editable=false rows are computed outputs.
export const IF1_ROWS: If1Row[] = [
  { prop: 'OwnFunds', label: 'Own Funds', editable: false, neg: false },
  { prop: 'Tier1', label: 'Tier 1 Capital', editable: false, neg: false },
  { prop: 'CET1', label: 'Common Equity Tier 1 Capital', editable: false, neg: false },
  { prop: 'FullyPaidUpCapitalInstruments', label: 'Fully Paid Up Capital Instruments', editable: true, neg: false },
  { prop: 'SharePremium', label: 'Share Premium', editable: true, neg: false },
  { prop: 'RetainedEarnings', label: 'Retained Earnings', editable: false, neg: false },
  { prop: 'PreviousYearsRetainedEarnings', label: 'Previous Years Retained Earning', editable: true, neg: false },
  { prop: 'ProfitEligible', label: 'Profit Eligible', editable: true, neg: false },
  { prop: 'AccumulatedOtherComprehensiveIncome', label: 'Accumulated Other Comprehensive Income', editable: true, neg: false },
  { prop: 'OtherReserves', label: 'Other Reserves', editable: true, neg: false },
  { prop: 'MinorityInterestGiven', label: 'Minority Interest Given Recognition in CET1', editable: true, neg: false },
  { prop: 'AdjustmentsToCET1', label: 'Adjustments to CET1 due to prudential filters', editable: true, neg: false },
  { prop: 'OtherFunds', label: 'Other Funds', editable: true, neg: false },
  { prop: '__TotalCET1Deductions', label: 'Total Deductions From Common Equity Tier 1', editable: false, neg: false },
  { prop: 'OwnCET1Instruments', label: 'Own CET1 Instruments', editable: true, neg: true },
  { prop: 'DirectHoldingsOfCET1Instruments', label: 'Direct Holdings Of CET1 Instruments', editable: true, neg: true },
  { prop: 'IndirectHoldingsOfCET1Instruments', label: 'Indirect Holdings Of CET1 Instruments', editable: true, neg: true },
  { prop: 'SyntheticHoldingsOfCET1Instruments', label: 'Synthetic Holdings Of CET1 Instruments', editable: true, neg: true },
  { prop: 'LossForYear', label: 'Losses For Current Financial Year', editable: true, neg: true },
  { prop: 'GoodWill', label: 'Goodwill', editable: true, neg: true },
  { prop: 'OtherIntangibleAssets', label: 'Other InTangible Assets', editable: true, neg: true },
  { prop: 'DeferredTaxAssets', label: 'Deferred Tax Assets That rely on Future Profitability and Do Not Aruse From Temporary Differences Ne of Associated Tax Liabilities', editable: true, neg: true },
  { prop: 'QualifyingHolding', label: 'Qualifying Holding Outside The Financial Sector Which Exceeds 15% of Own Funds', editable: true, neg: true },
  { prop: 'TotalQualifyingHolding', label: 'Total Qualifying Holdings in Undertaking Other Than Financial Sector Entities Which Exceeds 60% of Its Own Funds ', editable: true, neg: true },
  { prop: 'CET1InstrumentsNoSignificantInvestments', label: 'CET1 Instruments of Financial Sector Entities Where The Investment Firm Does Not Have a Significant Investment', editable: true, neg: true },
  { prop: 'CET1InstrumentsWithSignificantInvestments', label: 'CET1 Instruments of Financial Sector Entities Where The Investment Firm Has Significant Investment', editable: true, neg: true },
  { prop: 'DefinedBenefitPensionFundAssets', label: 'Defined Benefit Pension Fund Assets', editable: true, neg: true },
  { prop: 'OtherDeductions', label: 'Other Deductions', editable: true, neg: true },
  { prop: 'CET1OtherCapitalElements', label: 'CET1: Other Capital Elements, Deductions And Adjustments (Note 1)', editable: true, neg: false },
  { prop: 'AT1', label: 'Additional Tier 1 Capital', editable: false, neg: false },
  { prop: 'FullyPaidUpDirectCapitalInstruments', label: 'Fully Paid Up, Directly Issued Capital Instruments', editable: true, neg: false },
  { prop: 'SharePremiumAT1', label: 'Share Premium', editable: true, neg: false },
  { prop: '__TotalAT1Deductions', label: 'Total Deductions From Additional Tier 1', editable: false, neg: false },
  { prop: 'OwnAT1Instruments', label: 'Own AT1 Instruments', editable: true, neg: true },
  { prop: 'DirectHoldingsOfAT1Instruments', label: 'Direct Holdings Of AT1 Instruments', editable: true, neg: true },
  { prop: 'IndirectHoldingsOfAT1Instruments', label: 'Indirect Holdings Of AT1 Instruments', editable: true, neg: true },
  { prop: 'SyntheticHoldingsOfAT1Instruments', label: 'Synthetic Holdings Of AT1 Instruments', editable: true, neg: true },
  { prop: 'AT1InstrumentsNoSignificantInvestments', label: 'AT1 Instruments of Financial Sector Entities Where The Investment Firm Does Not Have a Significant Investment', editable: true, neg: true },
  { prop: 'AT1InstrumentsWithSignificantInvestments', label: 'AT1 Instruments of Financial Sector Entities Where The Investment Firm Has Significant Investment', editable: true, neg: true },
  { prop: 'OtherDeductionsAT1', label: 'Other Deductions', editable: true, neg: true },
  { prop: 'AdditionalTier1CapitalAT1', label: 'Additional Tier 1: Other Capital Elements, Deductions  And Adjustments', editable: true, neg: false },
  { prop: 'Tier2', label: 'Tier 2 Capital', editable: false, neg: false },
  { prop: 'FullyPaidUpDirectCapitalInstrumentsT2', label: 'Fully Paid Up, Directly Issued Capital Instruments', editable: true, neg: false },
  { prop: 'SharePremiumT2', label: 'Share Premium', editable: true, neg: false },
  { prop: '__TotalT2Deductions', label: 'Total Deductions From Additional Tier 2', editable: false, neg: false },
  { prop: 'OwnT2Instruments', label: 'Own T2 Instruments', editable: true, neg: true },
  { prop: 'DirectHoldingsOfT2Instruments', label: 'Direct Holdings Of T2 Instruments', editable: true, neg: true },
  { prop: 'IndirectHoldingsOfT2Instruments', label: 'Indirect Holdings Of T2 Instruments', editable: true, neg: true },
  { prop: 'SyntheticHoldingsOfT2Instruments', label: 'Synthetic Holdings Of T2 Instruments', editable: true, neg: true },
  { prop: 'T2InstrumentsNoSignificantInvestments', label: 'T2 Instruments of Financial Sector Entities Where The Investment Firm Does Not Have a Significant Investment', editable: true, neg: true },
  { prop: 'T2InstrumentsWithSignificantInvestments', label: 'T2 Instruments of Financial Sector Entities Where The Investment Firm Has Significant Investment', editable: true, neg: true },
  { prop: 'OtherDeductionsT2', label: 'Tier 2: Other Capital Elements, Deductions  And Adjustments', editable: true, neg: false },
];

export const IF1_EDITABLE_PROPS = IF1_ROWS.filter((r) => r.editable).map((r) => r.prop);
export const rowCode = (i: number) => String((i + 1) * 10).padStart(4, '0');

// Live recompute (mirrors if1.js calculate()): deductions are stored negative and ADDED.
export function if1Calc(v: Record<string, number>): Record<string, number> {
  const g = (k: string) => v[k] ?? 0;
  const totalT2 = g('OwnT2Instruments') + g('T2InstrumentsNoSignificantInvestments') + g('T2InstrumentsWithSignificantInvestments');
  const tier2 = g('FullyPaidUpDirectCapitalInstrumentsT2') + g('SharePremiumT2') + totalT2 + g('OtherDeductionsT2');
  const totalAT1 = g('OwnAT1Instruments') + g('AT1InstrumentsNoSignificantInvestments') + g('AT1InstrumentsWithSignificantInvestments') + g('OtherDeductionsAT1');
  const at1 = g('FullyPaidUpDirectCapitalInstruments') + g('SharePremiumAT1') + totalAT1 + g('AdditionalTier1CapitalAT1');
  const totalCET1 =
    g('OwnCET1Instruments') + g('LossForYear') + g('GoodWill') + g('OtherIntangibleAssets') + g('DeferredTaxAssets') +
    g('QualifyingHolding') + g('TotalQualifyingHolding') + g('CET1InstrumentsNoSignificantInvestments') +
    g('CET1InstrumentsWithSignificantInvestments') + g('DefinedBenefitPensionFundAssets') + g('OtherDeductions');
  const retained = g('PreviousYearsRetainedEarnings') + g('ProfitEligible');
  const cet1 =
    g('FullyPaidUpCapitalInstruments') + g('SharePremium') + retained + g('AccumulatedOtherComprehensiveIncome') +
    g('OtherReserves') + g('MinorityInterestGiven') + g('AdjustmentsToCET1') + g('OtherFunds') + totalCET1 + g('CET1OtherCapitalElements');
  const tier1 = cet1 + at1;
  return {
    OwnFunds: tier1 + tier2,
    Tier1: tier1,
    CET1: cet1,
    RetainedEarnings: retained,
    __TotalCET1Deductions: totalCET1,
    AT1: at1,
    __TotalAT1Deductions: totalAT1,
    Tier2: tier2,
    __TotalT2Deductions: totalT2,
  };
}

export const if1Fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString('en-US', { maximumFractionDigits: 20 }) : '0');
export const if1Parse = (s: string) => {
  const n = parseFloat(String(s).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};
